import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'bi-export-toast',
  templateUrl: './export-toast.component.html',
  styleUrl: './export-toast.component.scss',
  standalone: false,
})
export class ExportToastComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() sessionId = 0;
  @Input() progress = 0;
  @Input() activeCount = 0;
  @Input() processedRecords = 0;
  @Input() totalRecords = 0;
  @Input() completedCount = 0;
  @Input() isReady = false;
  @Input() downloadUrl = '';

  @Output() dismissed = new EventEmitter<void>();
  @Output() download = new EventEmitter<string>();
  @Output() openDownloads = new EventEmitter<void>();

  displayProgress = 0;

  private animationFrameId: number | null = null;
  private dismissedByUser = false;
  private sessionStarted = false;
  private completedPinned = false;
  private targetProgress = 0;
  private lastFrameTime = 0;
  private lastSessionId = 0;

  get showToast(): boolean {
    if (this.dismissedByUser) {
      return false;
    }

    if (this.completedPinned) {
      return true;
    }

    if (this.visible) {
      return true;
    }

    return this.sessionStarted && this.activeCount > 0;
  }

  get uiIsReady(): boolean {
    return this.completedPinned || this.isReady;
  }

  get showActiveCountBadge(): boolean {
    return !this.uiIsReady && this.activeCount > 1;
  }

  get titleText(): string {
    if (this.uiIsReady) {
      return this.completedCount > 1 ? 'Downloads Completed' : 'Download Completed';
    }

    if (this.activeCount > 1) {
      return 'Exporting Downloads';
    }

    return 'Exporting Data';
  }

  get subtitleText(): string {
    if (this.uiIsReady) {
      if (this.totalRecords > 0) {
        return `Export completed. Click to open`;
      }

      if (this.completedCount > 1) {
        return `${this.completedCount} files ready · Click to open`;
      }

      return 'Click to Download';
    }

    if (this.totalRecords > 0) {
      return `${this.formatNumber(this.processedRecords)} / ${this.formatNumber(this.totalRecords)} records`;
    }

    if (this.activeCount > 1) {
      return `${this.activeCount} exports in progress`;
    }

    return 'Preparing export…';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sessionId'] && this.sessionId !== this.lastSessionId) {
      this.lastSessionId = this.sessionId;
      this.dismissedByUser = false;
      this.completedPinned = false;
      this.sessionStarted = true;
      this.displayProgress = 0;
    }

    if (changes['visible']?.currentValue === true && changes['visible']?.previousValue === false) {
      this.dismissedByUser = false;
      this.completedPinned = false;
      this.sessionStarted = true;
      this.displayProgress = 0;
    }

    if (this.visible || this.activeCount > 0) {
      this.sessionStarted = true;
    }

    if (this.activeCount > 0 && this.visible) {
      this.dismissedByUser = false;
      this.completedPinned = false;
    }

    if (this.sessionStarted && !this.dismissedByUser && !this.completedPinned) {
      const exportFinished =
        this.isReady ||
        (this.activeCount === 0 && this.completedCount > 0) ||
        (this.activeCount === 0 && this.totalRecords > 0 && this.processedRecords >= this.totalRecords) ||
        (this.activeCount === 0 && this.progress >= 100 && this.sessionStarted);

      if (exportFinished) {
        this.pinCompletedState();
      }
    }

    this.targetProgress = this.uiIsReady
      ? 100
      : Math.min(100, Math.max(0, Math.round(this.progress)));

    if (this.showToast) {
      this.startProgressAnimation();
      return;
    }

    this.stopProgressAnimation();
  }

  ngOnDestroy(): void {
    this.stopProgressAnimation();
  }

  onDismiss(event: Event): void {
    event.stopPropagation();
    this.dismissedByUser = true;
    this.completedPinned = false;
    this.sessionStarted = false;
    this.displayProgress = 0;
    this.stopProgressAnimation();
    this.dismissed.emit();
  }

  onToastClick(): void {
    if (this.uiIsReady) {
      if (this.completedCount === 1 && this.downloadUrl) {
        this.download.emit(this.downloadUrl);
        window.open(this.downloadUrl, '_blank');
        return;
      }

      this.openDownloads.emit();
      return;
    }

    this.openDownloads.emit();
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat().format(value ?? 0);
  }

  private pinCompletedState(): void {
    this.completedPinned = true;
    this.targetProgress = 100;
    this.displayProgress = 100;
  }

  private startProgressAnimation(): void {
    if (this.animationFrameId !== null) {
      return;
    }

    this.lastFrameTime = performance.now();

    const tick = (timestamp: number) => {
      if (!this.showToast) {
        this.animationFrameId = null;
        return;
      }

      if (this.completedPinned) {
        this.displayProgress = 100;
        this.animationFrameId = requestAnimationFrame(tick);
        return;
      }

      const deltaMs = timestamp - this.lastFrameTime;
      this.lastFrameTime = timestamp;

      if (this.displayProgress < this.targetProgress) {
        const step = Math.max(1, Math.round((deltaMs / 450) * (this.targetProgress - this.displayProgress)));
        this.displayProgress = Math.min(this.targetProgress, this.displayProgress + step);
      } else {
        this.displayProgress = this.targetProgress;
      }

      this.animationFrameId = requestAnimationFrame(tick);
    };

    this.animationFrameId = requestAnimationFrame(tick);
  }

  private stopProgressAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
}
